import React, { useState, useRef, useEffect, useLayoutEffect } from "react";
import { gql, useMutation, useLazyQuery } from "@apollo/client";
import { useLocation, useNavigate } from "react-router-dom";
import SideBar from "../../components/SideBar";
import MuxPlayer from "@mux/mux-player-react";
import "./HowTo.css";
import BoundingBox from "../../components/video/BoundingBox";
import PointingArrow from "../../components/video/PointingArrow";
import { HelpCircle, ChevronLeft, X } from "lucide-react";
import { isMobileDevice } from "../../utils/deviceDetection";
import MobileRestrictionMessage from "../../components/MobileRestrictionMessage";

const SEEN_TUTORIAL = gql`
  mutation SeenTutorial {
	seenTutorial
  }
`;

const HAS_SEEN_TUTORIAL = gql`
  query HasSeenTutorial {
	hasSeenTutorial
  }
`;

const HowTo = () => {
	// Check if user is on mobile device - MUST be first hook
	const [isMobile, setIsMobile] = useState(false);

	useEffect(() => {
		const checkMobile = () => {
			setIsMobile(isMobileDevice());
		};

		// Check on mount
		checkMobile();

		// Check on window resize
		window.addEventListener('resize', checkMobile);
		
		return () => {
			window.removeEventListener('resize', checkMobile);
		};
	}, []);

	const location = useLocation();
	const backgroundVideoRef = useRef(null);
	const grabBiteBtnRef = useRef(null);
	const helpBtnRef = useRef(null);
	const [popupStep, setPopupStep] = useState(0);
	const [isGrabBite, setIsGrabBite] = useState(false);
	const [isPlaying, setIsPlaying] = useState(false);
	const [showBoundingBox, setShowBoundingBox] = useState(true);
	const [videoRect, setVideoRect] = useState(null);
    const [arrowPosition, setArrowPosition] = useState(null);
    const [isReturning, setIsReturning] = useState(false);

	const [animateBoundingBox, setAnimateBoundingBox] = useState(false);
	const isGuest =
		!!localStorage.getItem("accessToken") && !localStorage.getItem("idToken");
	const [showPopup, setShowPopup] = useState(true);

	const [seenTutorial] = useMutation(SEEN_TUTORIAL);
	const [hasSeenTutorial, { loading: tutLoading, error: tutError }] =
		useLazyQuery(HAS_SEEN_TUTORIAL, { fetchPolicy: "no-cache", skip: true });

    useEffect(() => {
		let isMounted = true; // cleanup flag

		 async function fetchData() {
			try {
				const { data } = await hasSeenTutorial();
				if (data.hasSeenTutorial === true) {
					setIsReturning(true);
				} else {
					setIsReturning(false);
				}
			} catch (err) {
				console.error("Error during tutorial check:", err);
			}
		}

		fetchData();

		return () => {
            isMounted = false; // avoid setting state after unmount
        };
	}, []); // runs once on mount

	useLayoutEffect(() => {
		window.addEventListener("resize", updateRect);
		// Listen for video metadata load
		const vidEl = backgroundVideoRef.current;
		if (vidEl) {
			vidEl.addEventListener("loadedmetadata", updateRect);
		}

		return () => {
			window.removeEventListener("resize", updateRect);
			if (vidEl) vidEl.removeEventListener("loadedmetadata", updateRect);
		};
	}, [videoRect]);

	useLayoutEffect(() => {
		window.addEventListener("resize", updateArrow);

	    if (popupStep === 9 || popupStep === 11) {
		    updateArrow()
	    }

		return () => {
			window.removeEventListener("resize", updateArrow);
		};
	}, [popupStep]);	const navigate = useNavigate();
	const handleClose = () => {
		setShowPopup(false);
		navigate("/home");
	};

	useEffect(() => {
		if (showPopup) document.body.classList.add("freeze-ui");
		else document.body.classList.remove("freeze-ui");
		return () => document.body.classList.remove("freeze-ui");
	  }, [showPopup]);

	// If mobile, show restriction message - AFTER all hooks are declared
	if (isMobile) {
		return <MobileRestrictionMessage />;
	}

	function updateRect() {
		if (!backgroundVideoRef.current) return;
		const vidEl = backgroundVideoRef.current;
		const cw = vidEl.clientWidth;
		const ch = vidEl.clientHeight;
		const vw = vidEl.videoWidth;
		const vh = vidEl.videoHeight;
		const cr = cw / ch;
		const vr = vw / vh;
		if (cr < vr) {
			// Contained is taller than video
			const nh = cw / vr;
			setVideoRect({
				left: 0,
				top: (ch - nh) / 2,
				width: cw,
				height: nh,
			});
		} else {
			// Contained is wider than video
			const nw = ch * vr;
			setVideoRect({
				left: (cw - nw) / 2,
				top: 0,
				width: nw,
				height: ch,
			});
		}
	}

	function updateArrow() {
		if (popupStep === 9 && grabBiteBtnRef.current) {
            const rect = grabBiteBtnRef.current.getBoundingClientRect();
		    setArrowPosition({
		        left: rect.left + window.scrollX - 290,
		        top: rect.top + window.scrollY + rect.height / 2 - 180,
		    });		
		}

		if (popupStep === 11 && helpBtnRef.current) {
			const rect = helpBtnRef.current.getBoundingClientRect();
		    setArrowPosition({
		        left: rect.right + window.scrollX - 110,
		        top: rect.top + window.scrollY + rect.height / 2 - 180,
		    });	
		}
	}

	const handleContinue = () => {
		if (popupStep === 4) {
			setShowBoundingBox(true);
			updateRect();
		}
		if (popupStep < 11) {
			setPopupStep(popupStep + 1);
		}
	};

	const togglePlayPause = (callback) => {
		const vid = backgroundVideoRef.current;
		if (!vid) return;
		vid.loop = false;
		if (vid.paused) {
			setIsPlaying(true);
			setTimeout(() => {
				vid.play();
				vid.onended = () => {
					callback();
					setIsPlaying(false);
				};
			}, 2000);
		} else {
			vid.pause();
			setIsPlaying(false);
		}
	};

    const setStepToToSeven = () => {
		setAnimateBoundingBox(true);
		togglePlayPause(() => {
			setPopupStep(7);
			setAnimateBoundingBox(false);
		});		
    }

	const stepSixToSeven = () => {
        setStepToToSeven();
	};

	const stepEightToSeven = () => {
		setStepToToSeven();	
	};

    const setStepToToEight = () => {
		setPopupStep(8);
		const vid = backgroundVideoRef.current;
		if (vid) {
			setIsPlaying(true);
			vid.play();
			vid.loop = true;
		}		
    }

	const stepSevenToEight = () => {
        setStepToToEight();
	};

    const stepNineToEight = () => {
		setStepToToEight();
    };

	const stepEightToNine = () => {
		setPopupStep(9);
	};

    const setStepToTen = () => {
		const vid = backgroundVideoRef.current;
		vid.pause();
		setIsPlaying(false);
		setPopupStep(10);
	};

	const stepNineToTen = () => {
        setStepToTen();
	};

	const stepElevenToTen = () => {
		setStepToTen();		
	};

	const recordSeenTutorial = async () => {
		if (!isGuest) {
			setIsReturning(true);
            const { data } = await seenTutorial();
	        if (data?.seenTutorial) {
				console.log("Recorded tutorial completion");
		    }
		}
	}

	const handleRewatch = () => {
		recordSeenTutorial();
		setPopupStep(0);
	};

	const handleFinish = async () => {
		recordSeenTutorial();
		navigate("/home");
	};

	const renderCloseButton = () => {
		if (!isReturning || isGuest) return null;

		// black from step 5 onward, otherwise brand yellow
		const xColor = popupStep >= 5 ? "#000000" : "#CBDF52";

		return (
			<button
				className="absolute top-4 right-4 hover:opacity-80"
				onClick={handleClose}
			>
				<X size={24} color={xColor} />
			</button>
		);
	};

	const renderBackButton = () => {
		if (!isReturning || isGuest) return null;

		return (
			<div className="absolute bottom-14 left-0 right-0 px-12 flex justify-between items-center">
				<button
				    onClick={() => setPopupStep(popupStep - 1)}
					className="flex items-center space-x-2 text-[#CBDF52] text-2xl font-bold hover:opacity-80"
				>
					<ChevronLeft size={24} />
					<span>Back</span>
			    </button>
            </div>
		);
	};

	const activeTab = location.pathname === "/howto" ? "howto" : "home";

	return (
		<div className="flex w-full h-screen relative bg-[#232222]">
			<div className="z-10 pointer-events-none">
				<SideBar activeTab={activeTab} />
			</div>

			<div className="flex-1 p-6 relative z-10 overflow-y-auto">
				<div className="flex justify-between items-center mb-6">
					<h1 className="text-3xl font-semibold text-white">
						{isReturning && !isGuest ? (
							<span>Welcome back...</span>
						) : (
							<span>Welcome to Pounce!</span>
						)}
					</h1>
				</div>

				{/* HomePage Video Section UI */}
				<section className="bg-[#232323] p-4 rounded-lg shadow-md mb-8">
					<h2 className="text-xl font-semibold text-white mb-4">
						Latest Video
					</h2>

					<div className="relative flex justify-center">
						<MuxPlayer
							playbackId="JfCz7Ivg2lPWHOQxjMad00EgNNDtjPEVdyFeuCq2iV00I"
							metadataVideoTitle="Main how-to video"
							primaryColor="#ffffff"
							secondaryColor="#000000"
							accentColor="#fa50b5"
							controls={false}
							loop={false}
							muted
							height="auto"
							style={{
								height: "70vh",
								"--center-play-button": "none",
								"--bottom-controls": "none",
							}}
							className="mx-auto rounded-lg border border-gray-500 aspect-video w-full"
							ref={(el) => (backgroundVideoRef.current = el)}
							onTimeUpdate={(e) => {
								const currentTime = e.target.currentTime;
								if (currentTime >= 3.5 && currentTime < 5.9) {
									// console.log("pup start grab-bitting");
									setIsGrabBite(true);
								} else {
									// console.log("pup stopped grab-bitting");
									setIsGrabBite(false);
								}
							}}
						/>

						{popupStep === 5 && (
							<>
								{videoRect && (
									<BoundingBox
										location={{
											left: videoRect.left + 0.6 * videoRect.width,
											top: videoRect.top + 0.58 * videoRect.height,
										}}
										size={{
											width: 0.11 * videoRect.width,
											height: 0.3 * videoRect.height,
										}}
									/>
								)}

								{/* White Instruction Popup */}
								<div
									className="allow-interact absolute top-40 left-8 bg-white text-black rounded-2xl shadow-4xl z-40"
									style={{ width: 378, height: 212, padding: 24 }}
								>
									<div className="h-full flex flex-col justify-center items-center">
										{renderCloseButton()}
										{/* Instruction Text */}
										<p className="text-2xl font-bold leading-snug text-center mb-4">
											Only watch the pup shown <br /> in the highlighted square
										</p>

										{/* Footer: Always show Continue; Back only for returning non‑guest users */}
										<div className="w-full flex justify-between px-4">
											{/* ← Back */}
											{isReturning && !isGuest && (
												<button
													onClick={() => setPopupStep(4)}
													className="flex items-center space-x-2 text-gray-700 hover:opacity-80"
												>
													<ChevronLeft size={20} />
													<span className="font-medium">Back</span>
												</button>
											)}

											{/* Continue */}
											<div className="w-full flex justify-center">
												<button
													onClick={() => setPopupStep(6)}
													className="bg-[#F0F0F0] text-black font-bold text-xl py-3 px-6 rounded shadow hover:scale-105 transition"
												>
													Continue
												</button>
											</div>
										</div>
									</div>
								</div>
							</>
						)}

						{popupStep === 6 && (
							<>
								{videoRect && (
									<BoundingBox
										location={{
											left: videoRect.left + 0.6 * videoRect.width,
											top: videoRect.top + 0.58 * videoRect.height,
										}}
										size={{
											width: 0.11 * videoRect.width,
											height: 0.3 * videoRect.height,
										}}
										animate={animateBoundingBox}
									/>
								)}

								{/* Step 6 popup */}
								<div
									className="allow-interact absolute top-40 left-8 bg-white text-black rounded-xl shadow-4xl z-40"
									style={{ width: 400, height: 212, padding: 24 }}
								>
									<div className="h-full flex flex-col justify-center items-center">
										{renderCloseButton()}
										{/* Instruction text */}
										<p className="text-2xl font-bold leading-snug text-center mb-4">
											The highlighted square <br /> will disappear, but keep{" "}
											<br /> your eyes on that pup!
										</p>

										{/* Footer: Always show Play; Back only for returning non‑guest users */}
										<div className="w-full flex justify-between px-4">
											{/* ← Back */}
											{isReturning && !isGuest && (
												<button
													onClick={() => setPopupStep(5)}
													className="flex items-center space-x-2 text-gray-700 hover:opacity-80"
												>
													<ChevronLeft size={20} />
													<span className="font-medium">Back</span>
												</button>
											)}

											{/* Play video (always centered) */}
											{!isPlaying && (
												<div className="w-full flex justify-center mt-4">
													<button
														onClick={stepSixToSeven}
														className="bg-[#F0F0F0] text-black text-lg font-semibold py-3 px-6 rounded shadow hover:scale-105 transition"
													>
														Play video
													</button>
												</div>
											)}
										</div>
									</div>
								</div>
							</>
						)}

						{showPopup && popupStep === 7 && (
							<div
								className="allow-interact absolute top-40 left-8 bg-white text-black rounded-xl shadow-4xl z-40"
								style={{ width: 400, height: 212, padding: 24 }}
							>
								<div className="h-full flex flex-col justify-center items-center">
									{renderCloseButton()}
									{/* Instruction */}
									<p className="text-2xl font-bold leading-snug text-center mb-4">
										The highlighted square <br /> will disappear, but keep{" "}
										<br /> your eyes on that pup!
									</p>

									{/* Footer */}
									<div className="w-full flex items-center justify-center space-x-4 px-4">
										{/* ← Back (only for returning non‑guest users) */}
										{isReturning && !isGuest && (
											<button
												onClick={() => setPopupStep(6)}
												className="flex items-center space-x-2 text-gray-700 hover:opacity-80"
											>
												<ChevronLeft size={20} />
												<span className="font-medium">Back</span>
											</button>
										)}

										{/* Continue (always shown, centered) */}
										<button
											onClick={stepSevenToEight}
											className="bg-[#F0F0F0] text-black text-lg font-semibold py-3 px-6 rounded shadow hover:scale-105 transition"
										>
											Continue
										</button>
									</div>
								</div>
							</div>
						)}

						{showPopup && popupStep === 8 && (
							<>
								{isGrabBite && videoRect && (
									<PointingArrow location={{ left: videoRect.left + 0.55 * videoRect.width, 
										                       top: videoRect.top + 0.59 * videoRect.height }} />
								)}

								{/* Step 8 popup */}
								<div
									className="allow-interact absolute top-40 left-8 bg-white text-black rounded-xl shadow-4xl z-40"
									style={{ width: 400, height: 212, padding: 24 }}
								>
									<div className="h-full flex flex-col justify-center items-center">
										{renderCloseButton()}
										{/* Instruction */}
										<p className="text-2xl font-bold leading-snug text-center mb-4">
											This pup is grab‑biting!
										</p>

										{/* Footer */}
										<div className="w-full flex items-center justify-center space-x-4 px-4">
											{/* ← Back (only for returning non‑guest users) */}
											{isReturning && !isGuest && (
												<button
													onClick={() => stepEightToSeven()}
													className="flex items-center space-x-2 text-gray-700 hover:opacity-80"
												>
													<ChevronLeft size={20} />
													<span className="font-medium">Back</span>
												</button>
											)}

											{/* Continue (always shown) */}
											<button
												onClick={stepEightToNine}
												className="bg-[#F0F0F0] text-black text-lg font-semibold py-3 px-6 rounded shadow hover:scale-105 transition"
											>
												Continue
											</button>
										</div>
									</div>
								</div>
							</>
						)}

						{showPopup && popupStep === 9 && (
							<>
                                {isGrabBite && arrowPosition && (
                                   <PointingArrow location={{ left: arrowPosition.left, top: arrowPosition.top, position: "absolute" }} />
                                )}

								{/* Step 9 popup */}
								<div
									className="allow-interact absolute top-40 left-8 bg-white text-black rounded-xl shadow-4xl z-40"
									style={{ width: 400, height: 212, padding: 24 }}
								>
									<div className="h-full flex flex-col justify-center items-center">
										{renderCloseButton()}
										{/* Instruction */}
										<p className="text-xl font-bold leading-snug text-center mb-4">
											Mark the moment you see the pup grab-bite by clicking the button.
										</p>

										{/* Footer */}
										<div className="w-full flex items-center justify-center space-x-4 px-4">
											{/* ← Back (only returning non‑guest) */}
											{isReturning && !isGuest && (
												<button
													onClick={() => stepNineToEight()}
													className="flex items-center space-x-2 text-gray-700 hover:opacity-80"
												>
													<ChevronLeft size={20} />
													<span className="font-medium">Back</span>
												</button>
											)}

											{/* Continue */}
											<button
												onClick={stepNineToTen}
												className="bg-[#F0F0F0] text-black text-lg font-semibold py-3 px-6 rounded shadow hover:scale-105 transition"
											>
												Continue
											</button>
										</div>
									</div>
								</div>
							</>
						)}

						{showPopup && popupStep === 10 && (
							<div
								className="allow-interact absolute top-40 left-8 bg-white text-black rounded-xl shadow-4xl z-40"
								style={{ width: 400, height: 212, padding: 24 }}
							>
								<div className="h-full flex flex-col justify-center items-center">
									{renderCloseButton()}
									{/* Message */}
									<p className="text-2xl font-bold leading-snug text-center mb-4">
										In a video, the pup may <br /> grab-bite many times, or{" "}
										<br />
										never. That's okay!
									</p>

									{/* Footer */}
									<div className="w-full flex items-center justify-center space-x-4 px-4">
										{/* ← Back (only for returning non‑guest users) */}
										{isReturning && !isGuest && (
											<button
												onClick={() => setPopupStep(9)}
												className="flex items-center space-x-2 text-gray-700 hover:opacity-80"
											>
												<ChevronLeft size={20} />
												<span className="font-medium">Back</span>
											</button>
										)}

										{/* Continue */}
										<button
											onClick={() => setPopupStep(11)}
											className="bg-[#F0F0F0] text-black text-lg font-semibold py-3 px-6 rounded shadow hover:scale-105 transition"
										>
											Continue
										</button>
									</div>
								</div>
							</div>
						)}

						{showPopup && popupStep === 11 && (
							<>
								{/* <PointingArrow location={{ left: "58%", top: "104%" }} rotate /> */}

                                {arrowPosition && (
                                   <PointingArrow location={{ left: arrowPosition.left, top: arrowPosition.top, position: "absolute" }} rotate />
                                )}

								<div
									className="allow-interact absolute top-40 left-8 bg-white text-black rounded-xl shadow-4xl text-center z-40"
									style={{ width: 400, height: 212, padding: 24 }}
								>
									<div className="flex flex-col justify-center items-center h-full gap-4">
										{renderCloseButton()}
										{/* Instruction */}
										<p className="text-2xl font-bold leading-snug text-center">
											To review the definition of <br /> grab‑bite, click here.
										</p>

										{/* Footer */}
										<div className="w-full flex items-center justify-center space-x-4 px-4">
											{/* ← Back (only returning non‑guest) */}
											{isReturning && !isGuest && (
												<button
													onClick={() => stepElevenToTen()}
													className="flex items-center space-x-2 text-gray-700 hover:opacity-80"
												>
													<ChevronLeft size={20} />
													<span className="font-medium">Back</span>
												</button>
											)}

											{/* Continue */}
											<button
												onClick={() => setPopupStep(12)}
												className="bg-[#F0F0F0] text-black text-lg font-semibold py-3 px-6 rounded shadow hover:scale-105 transition"
											>
												Continue
											</button>
										</div>
									</div>

									{/* placeholder arrow container */}
									<div className="absolute bottom-[-600px] left-[47%] translate-x-[-50%]" />
								</div>
							</>
						)}
					</div>

					<div
						className="absolute"
						style={{
							left: "54.9%",
							top: "59.98%",
							width: "16.56%",
							height: "22.69%",
							pointerEvents: "none",
						}}
					></div>

					<p className="text-gray-400 text-sm mt-2 text-center">
						Tip: Click 'Grab-Bite' every time you see the puppy do this during
						the 1-minute video.
					</p>

					<div className="mt-4 flex items-center justify-center space-x-4">
						<button
						    ref={grabBiteBtnRef}
							className={`border-2 border-[#C6E675] bg-[#2F2D2D] font-bold py-4 px-10 text-lg rounded-xl transition ${
							isGrabBite && popupStep === 9 ? "ring-4 ring-[#C6E675] animate-pulse" : "" } text-[#C6E675]`}
						>
							Grab-bite
						</button>

						<button 
						    ref={helpBtnRef}
						    className="p-3 rounded-full flex items-center justify-center bg-[#C6E675] shadow-lg transition hover:scale-105"
						>
							<HelpCircle size={32} color="black" strokeWidth={2.5} />
						</button>
					</div>

					{showPopup && popupStep === 0 && (
						<div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-60">
                            <div className="allow-interact bg-[#2F2D2D] text-white rounded-2xl p-4 sm:p-8 max-w-4xl w-[90%] min-h-[300px] md:min-h-[500px] relative text-center shadow-2xl border border-gray-600 overflow-y-auto">
								{/* {console.log("isReturning", isReturning)} */}
								{renderCloseButton()}

								{/* Title */}
								<h2 className="text-5xl font-bold mb-4 text-[#CBDF52]">
									Welcome to Pounce!
								</h2>
								{/* Description */}
								<p className="text-white text-xl">
									Unleash your inner scientist by watching adorable puppies in action!
									<br />
									<br />
									<span className="text-white text-lg">
										In this tutorial, you'll watch short videos of puppies and
										learn how to mark a specific behavior called grab-bite.
									</span>
								</p>

								<div className="mb-6 flex justify-center">
									<img
										src="/assets/images/puppy-2.png"
										alt="Cute Puppy"
										className="w-40 object-contain"
									/>
								</div>									

								{/* Progress Dots */}
								<div className="flex justify-center space-x-2 mb-6">
									{[0, 1, 2, 3, 4].map((index) => (
										<span
											key={index}
											className={`w-3 h-3 rounded-full ${
												index === popupStep ? "bg-[#C6E675]" : "bg-gray-500"
											}`}
										/>
									))}
								</div>

								{/* Continue Button */}
								<button
									onClick={handleContinue}
									className="bg-[#CBDF52] text-black font-semibold py-4 px-40 rounded-lg shadow hover:scale-105 transition"
								>
									Continue
								</button>
							</div>
						</div>
					)}

					{showPopup && popupStep === 1 && (
						<div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-60">
							<div className="allow-interact bg-[#2F2D2D] text-white rounded-2xl p-4 sm:p-8 max-w-4xl w-[90%] min-h-[300px] md:min-h-[500px] relative text-center shadow-2xl border border-gray-600 overflow-y-auto">
								{renderCloseButton()}
							    {renderBackButton()}

								{/* Title */}
								<h2 className="text-5xl font-bold mb-4 text-[#CBDF52]">
									What is grab-bite?
								</h2>

								{/* Description */}
								<p className="text-white text-xl mb-4">
									Grab-bite is when a pup snaps jaws around object or other
									puppy.
								</p>

								{/* Puppy Image */}
								<div className="mb-6 flex justify-center">
									<img
										src="/assets/images/puppy-2.png"
										alt="Grab-bite Illustration"
										className="w-72 h-52 object-contain"
									/>
								</div>

								{/* Progress Dots */}
								<div className="flex justify-center space-x-2 mb-6">
									{[0, 1, 2, 3, 4].map((index) => (
										<span
											key={index}
											className={`w-3 h-3 rounded-full ${
												index === popupStep ? "bg-[#C6E675]" : "bg-gray-500"
											}`}
										/>
									))}
								</div>

								{/* Continue Button */}
								<button
									onClick={handleContinue}
									className="bg-[#CBDF52] text-black font-semibold py-4 px-40 rounded-lg shadow hover:scale-105 transition"
								>
									Continue
								</button>
							</div>
						</div>
					)}

					{showPopup && popupStep === 2 && (
						<div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-60">
							{/* <div className="bg-[#2F2D2D] text-white rounded-2xl p-12 max-w-4xl w-[90%] min-h-[500px] relative text-center shadow-2xl border border-gray-600"> */}
							<div className="allow-interact bg-[#2F2D2D] text-white rounded-2xl p-4 sm:p-8 max-w-4xl w-[90%] min-h-[300px] md:min-h-[500px] relative text-center shadow-2xl border border-gray-600 overflow-y-auto">
								{renderCloseButton()}
								{renderBackButton()}

								{/* Title */}
								<h2 className="text-5xl font-extrabold mb-6 text-[#CBDF52]">
									This is what grab-bite looks like
								</h2>

								{/* Example Video */}
								<div className="h-max-400px">
									<MuxPlayer
										playbackId="Sf1ZLAykkwuX028cs2v6xe00RVqV7Lg01t8lqVDnB005028A"
										metadataVideoTitle="Grab-bite example with animation"
										primaryColor="#ffffff"
										secondaryColor="#000000"
										accentColor="#fa50b5"
										muted
										autoPlay
										streamType="on-demand"
										controls={false}
										loop
										className="mx-auto rounded-lg aspect-video w-full"
										style={{
											"height": "60vh",
											"--center-play-button": "none",
											"--bottom-controls": "none",
										}}
									/>
								</div>

								{/* Progress Dots */}
								<div className="flex justify-center space-x-2 mb-6">
									{[0, 1, 2, 3, 4].map((index) => (
										<span
											key={index}
											className={`w-3 h-3 rounded-full ${
												index === popupStep ? "bg-[#C6E675]" : "bg-gray-500"
											}`}
										/>
									))}
								</div>

								{/* Continue Button (always visible) */}
								<button
									onClick={handleContinue}
									className="bg-[#CBDF52] text-black font-semibold py-3 px-20 rounded-lg shadow hover:scale-105 transition"
								>
									Continue
								</button>
							</div>
						</div>
					)}

					{showPopup && popupStep === 3 && (
						<div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-60">
							<div className="allow-interact bg-[#2F2D2D] text-white rounded-2xl p-12 max-w-4xl w-[90%] min-h-[500px] relative text-center shadow-2xl border border-gray-600">
								{renderCloseButton()}
								{renderBackButton()}

								{/* Title */}
								<h2 className="text-5xl font-bold mb-6 text-[#CBDF52]">
									Example of pups grab-biting
								</h2>

								{/* Video */}
								<div className="h-max-400px">
									<MuxPlayer
										playbackId="Mt9bJXWXnLWZn01Ng01HawdDUaMQ6viPQ00jtY7TJcVFck"
										metadataVideoTitle="Grab-bite example with real pups"
										primaryColor="#ffffff"
										secondaryColor="#000000"
										accentColor="#fa50b5"
										muted
										autoPlay
										streamType="on-demand"
										controls={false}
										loop
										className="mx-auto rounded-lg aspect-video w-full"
										style={{
											"height": "60vh",
											"--center-play-button": "none",
											"--bottom-controls": "none",
										}}
									/>
								</div>

								{/* Dots */}
								<div className="flex justify-center space-x-2 mb-6">
									{[0, 1, 2, 3, 4].map((index) => (
										<span
											key={index}
											className={`w-3 h-3 rounded-full ${
												index === popupStep ? "bg-[#C6E675]" : "bg-gray-500"
											}`}
										/>
									))}
								</div>

								{/* Continue Button (always visible) */}
								<button
									onClick={handleContinue}
									className="bg-[#CBDF52] text-black font-semibold py-4 px-40 rounded-lg shadow hover:scale-105 transition"
								>
									Continue
								</button>
							</div>
						</div>
					)}

					{showPopup && popupStep === 4 && (
						<div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-60">
							<div className="allow-interact bg-[#2F2D2D] text-white rounded-2xl p-12 max-w-4xl w-[90%] min-h-[500px] relative text-center shadow-2xl border border-gray-600">
								{renderCloseButton()}
								{renderBackButton()}

								{/* Title */}
								<h2 className="text-4xl font-bold mb-6 text-[#CBDF52]">
									Grab-bite is NOT gnawing or chewing
								</h2>

								{/* Video */}
                                <div className="flex justify-center mb-6">
									<div className="h-max-400px">
										<MuxPlayer
											playbackId="hBvD4IvsCkDbo5wlnyvxDVInQVRbBpVt6Am8xzckrbQ"
											metadataVideoTitle="Gnawing example with real pup"
											primaryColor="#ffffff"
											secondaryColor="#000000"
											accentColor="#fa50b5"
										muted
										autoPlay
										streamType="on-demand"
										controls={false}
										loop
										// className="mx-auto rounded-lg border border-gray-500 aspect-video w-full"
										style={{
											"height": "60vh",
											"--center-play-button": "none",
											"--bottom-controls": "none",
										}}
										/>
									</div>
								</div>								

								{/* Dots */}
								<div className="flex justify-center space-x-2 mb-6">
									{[0, 1, 2, 3, 4].map((index) => (
										<span
											key={index}
											className={`w-3 h-3 rounded-full ${
												index === popupStep ? "bg-[#C6E675]" : "bg-gray-500"
											}`}
										/>
									))}
								</div>

								{/* Continue Button (always visible) */}
								<button
									onClick={handleContinue}
									className="bg-[#CBDF52] text-black font-semibold py-4 px-40 rounded-lg shadow hover:scale-105 transition"
								>
									Continue
								</button>
							</div>
						</div>
					)}

					{showPopup && popupStep === 12 && (
						<div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-60">
							<div className="allow-interact bg-[#2F2D2D] text-white rounded-2xl p-12 max-w-4xl w-[90%] min-h-[500px] relative text-center shadow-2xl border border-gray-600">
								<h2 className="text-5xl font-bold mb-6 text-[#CBDF52]">
									Your turn!
								</h2>
								<p className="text-white text-xl py-10">
									You’re all set to start watching puppy videos. 
									Remember, mark each time you see the highlighted pup grab-bite either an object or other pup. 
									Get ready to pounce into the world of playful pups!
								</p>

								<div className="flex w-full gap-10 mt-10">
									<div className="w-[50%]">
										<button
											onClick={handleRewatch}
											className="w-full border-2 border-[#C6E675] bg-[#2F2D2D] font-bold py-4 px-10 text-lg rounded-xl text-[#C6E675] hover:scale-105 transition"
										>
											Rewatch tutorial
										</button>
									</div>
									<div className="w-[50%]">
										<button
											onClick={handleFinish}
											className="w-full text-lg bg-[#CBDF52] border-2 border-[#C6E675] font-bold text-black py-4 px-4 rounded-lg shadow hover:scale-105 transition"
										>
											Watch Puppies!
										</button>
									</div>
								</div>
							</div>
						</div>
					)}
				</section>
			</div>
		</div>
	);
};

export default HowTo;
